import { ConnectionType } from '@capacitor/network';
import React from 'react';
import { useSelector } from 'react-redux';
import { selectAuthUserInfo } from 'src/redux/auth/selectors';
import { selectNetworkState } from 'src/redux/common/selectors';
import { useAuthToken } from './useAuthToken';

/** Describes the ways in which the app functionality may be limited. */
export enum AppLimitedMode {
  Unauthenticated = 'unauthenticated',
  Offline = 'offline',
  SessionExpired = 'session-expired',
  NoUserInfo = 'no-user-info',
}

interface LimitedModeResult {
  /** The reason that the app is currently in the limited state. */
  mode?: AppLimitedMode;
  /** Whether or not the app is in a limited state. */
  isLimited: boolean;
}

/**
 * A hook that returns information about whether or not the app is in a "limited" state.
 * Network requests should not be made in this state.
 */
export const useLimitedMode = (): LimitedModeResult => {
  const authToken = useAuthToken();
  const network = useSelector(selectNetworkState);
  const userInfo = useSelector(selectAuthUserInfo);

  const mode = React.useMemo<AppLimitedMode | undefined>(() => {
    return calculatedLimitedMode(authToken.isAuthenticated, authToken.isExpired === true, network, !!userInfo);
  }, [authToken.isAuthenticated, authToken.isExpired, network, userInfo]);

  const isLimited = !!mode;

  return React.useMemo(() => ({ mode, isLimited }), [isLimited, mode]);
};

export const calculatedLimitedMode = (
  isAuthenticated: boolean,
  isSessionExpired: boolean,
  networkState: ConnectionType,
  isUserInfoLoaded: boolean,
): AppLimitedMode | undefined => {
  if (!isAuthenticated) return AppLimitedMode.Unauthenticated;
  if (!isUserInfoLoaded) return AppLimitedMode.NoUserInfo;
  if (networkState === 'none') return AppLimitedMode.Offline;
  if (isSessionExpired) return AppLimitedMode.SessionExpired;
  return undefined;
};
